import clsx from 'clsx';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import {ERROR, FALLBACK} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import HalfDonutChart from '@di-shared/features/common/HalfDonutChart';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useDrivingInfoKr from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import useHistoryLock from '@di-shared/features/history/setting/useHistoryLock';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

const FUEL_EFFICIENCY_GRAPH = {
  MAX: 30,
  EMPTY_RATIO: 0,
  FULL_RATIO: 1,
  WIDTH: 100,
  STROKE_WIDTH: 8,
  COLOR: 'var(--secondary-mid)',
  TEXT: {left: '0', right: '30', offset: 4, fontSize: 10},
};

export default function FuelEfficiencySectionKr({vin}: {vin: string}) {
  const {isHistoryUnlock} = useHistoryLock({vin});
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {isSameFuelAgreeMonth, isCurrentDrivingDistanceNone} = useDrivingInfoKr({vin});

  // 주행기록 잠금
  if (!isHistoryUnlock) {
    return <FuelEfficiencySectionLock vin={vin} />;
  }

  // 누적주행거리 ((연비 지원 && 연비 동의 당월) || 연비 미지원)
  if (
    checkAvailableComponent(PermissionTypeKr.FUEL_EFFICIENCY)
      ? isSameFuelAgreeMonth
      : !checkAvailableComponent(PermissionTypeKr.FUEL_EFFICIENCY)
  ) {
    return <FuelEfficiencySectionOdometer vin={vin} />;
  }

  // 당월 주행없음
  if (isCurrentDrivingDistanceNone) {
    return <FuelEfficiencySectionNone vin={vin} />;
  }

  // 당월 연비
  return <FuelEfficiencySectionDefault vin={vin} />;
}

// FALLBACK(LOADING, ERROR) UI
FuelEfficiencySectionKr.Error = () => <FuelEfficiencySectionFallback fallback={FALLBACK.ERROR} />;
FuelEfficiencySectionKr.Loading = () => <FuelEfficiencySectionFallback fallback={FALLBACK.LOADING} />;

function FuelEfficiencySectionFallback({fallback}: {fallback: FALLBACK}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultFuelEfficiencySymbol} = useUnitConverter();

  return (
    <div className={clsx('section_container trip_month_efficiency', {skeleton: fallback === FALLBACK.LOADING})}>
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <div className="trip_month_info">
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultFuelEfficiencySymbol}</span>
        </div>
        <span className="trip_month_compare">
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultFuelEfficiencySymbol}</span>
        </span>
        <div className="trip_month_graph_area">
          <div className="efficiency_graph_wrap">
            <div className="efficiency_graph">
              <HalfDonutChart
                color={FUEL_EFFICIENCY_GRAPH.COLOR}
                ratio={FUEL_EFFICIENCY_GRAPH.EMPTY_RATIO}
                width={FUEL_EFFICIENCY_GRAPH.WIDTH}
                strokeWidth={FUEL_EFFICIENCY_GRAPH.STROKE_WIDTH}
                text={FUEL_EFFICIENCY_GRAPH.TEXT}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FuelEfficiencySectionLock({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {isHistoryUnlock} = useHistoryLock({vin});

  if (isHistoryUnlock === null) {
    return <FuelEfficiencySectionKr.Loading />;
  }

  return (
    <div className={clsx('section_container trip_month_efficiency')}>
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          });
        }}
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10191')}
        </p>
        <div className="trip_month_graph_area">
          <div className="efficiency_graph_wrap">
            <div className="efficiency_graph">
              <HalfDonutChart
                color={FUEL_EFFICIENCY_GRAPH.COLOR}
                ratio={FUEL_EFFICIENCY_GRAPH.EMPTY_RATIO}
                width={FUEL_EFFICIENCY_GRAPH.WIDTH}
                strokeWidth={FUEL_EFFICIENCY_GRAPH.STROKE_WIDTH}
                text={FUEL_EFFICIENCY_GRAPH.TEXT}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FuelEfficiencySectionOdometer({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const {drivingData} = useDrivingInfoKr({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  return (
    <div className="section_container trip_month_efficiency">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T10033'),
          });
        }}
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10033')}</h3>
      </div>
      <div className="trip_month">
        <div className="trip_month_info">
          <span className="trip_month_value">{formatNumberWithCommas(convertDistanceValue(drivingData.odometer))}</span>
          <span className="trip_month_unit">{convertDistanceSymbol(drivingData.odometer)}</span>
        </div>
        <p className="trip_month_desc">{t('T10163')}</p>
        <div className="trip_month_imgbox">
          <ImageSelector
            kiaLightSrc={() => import('@di-shared/assets/img/home/driving_record_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/driving_record.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/driving_record_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/driving_record.png')}
          />
        </div>
      </div>
    </div>
  );
}

function FuelEfficiencySectionNone({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <div className={clsx('section_container trip_month_efficiency')}>
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() => {
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          });
        }}
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10186')}
        </p>
        <div className="trip_month_graph_area">
          <div className="efficiency_graph_wrap">
            <div className="efficiency_graph">
              <HalfDonutChart
                color={FUEL_EFFICIENCY_GRAPH.COLOR}
                ratio={FUEL_EFFICIENCY_GRAPH.EMPTY_RATIO}
                width={FUEL_EFFICIENCY_GRAPH.WIDTH}
                strokeWidth={FUEL_EFFICIENCY_GRAPH.STROKE_WIDTH}
                text={FUEL_EFFICIENCY_GRAPH.TEXT}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FuelEfficiencySectionDefault({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {drivingData, currentFuelEfficiency} = useDrivingInfoKr({vin});
  if (!currentFuelEfficiency) {
    throw new ExpectedError('currentFuelEfficiency error');
  }

  const averageFuelEfficiencyCompare = currentFuelEfficiency.avgFuelEfficiency - drivingData.avgFuelEfficiency;

  const {convertFuelEfficiencySymbol, convertFuelEfficiencyValue} = useUnitConverter();
  return (
    <div className="section_container trip_month_efficiency">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          })
        }
      />

      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T20059_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month">
        <div className="trip_month_info">
          <span className="trip_month_value">
            {formatNumberWithCommas(convertFuelEfficiencyValue(currentFuelEfficiency.avgFuelEfficiency))}
          </span>
          <span className="trip_month_unit">{convertFuelEfficiencySymbol(currentFuelEfficiency.avgFuelEfficiency)}</span>
        </div>
        <span className="trip_month_compare">
          <span
            className={clsx('ic', {
              ic_di_count_up: averageFuelEfficiencyCompare >= 0,
              ic_di_count_down: averageFuelEfficiencyCompare < 0,
            })}
            aria-hidden="true"
          >
            <span className="blind">{averageFuelEfficiencyCompare >= 0 ? t('T10059') : t('T10058')}</span>
          </span>
          <span className="trip_month_value">
            {formatNumberWithCommas(convertFuelEfficiencyValue(Math.abs(averageFuelEfficiencyCompare)))}
          </span>
          <span className="trip_month_unit">{convertFuelEfficiencySymbol(Math.abs(averageFuelEfficiencyCompare))}</span>
        </span>
        <p className="trip_month_desc">
          {averageFuelEfficiencyCompare === 0 ? t('T20107') : averageFuelEfficiencyCompare > 0 ? t('T20209') : t('T20210')}
        </p>
      </div>
      <div className="trip_month_graph_area">
        <div className="efficiency_graph_wrap">
          <div className="efficiency_graph">
            <span className="speech_bubble">{formatNumberWithCommas(currentFuelEfficiency.avgFuelEfficiency)}</span>
            <HalfDonutChart
              color={FUEL_EFFICIENCY_GRAPH.COLOR}
              ratio={
                currentFuelEfficiency.avgFuelEfficiency
                  ? currentFuelEfficiency.avgFuelEfficiency / FUEL_EFFICIENCY_GRAPH.MAX > FUEL_EFFICIENCY_GRAPH.FULL_RATIO
                    ? FUEL_EFFICIENCY_GRAPH.FULL_RATIO
                    : currentFuelEfficiency.avgFuelEfficiency / FUEL_EFFICIENCY_GRAPH.MAX
                  : FUEL_EFFICIENCY_GRAPH.EMPTY_RATIO
              }
              width={FUEL_EFFICIENCY_GRAPH.WIDTH}
              strokeWidth={FUEL_EFFICIENCY_GRAPH.STROKE_WIDTH}
              text={FUEL_EFFICIENCY_GRAPH.TEXT}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
